import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AbstractModel, IAbstractModel } from "model/abstract.model";
import { IConsumidor } from "model/cliente.model";
import { Preferencias } from "pages/cadastro-pessoa/pessoa.model";
import { ICpfNota } from "pages/cpfNota/CpfNota";
import { TDadosTransportador } from "pages/DadosTransportador";
import { FlowVendedorDTO } from "pages/registra-venda/OutrosComissionados";
import { EstoqueTO, FlowVendaTO, IProduto, IProdutoRow, PedidoItemDesconto, StatusPedido, TipoDesconto } from "pages/registra-venda/registra-venda.model";
import { SourceType } from "react-number-format/types/types";
import { calcularPorcentagemDesconto, calcularValorDesconto, getDescItem, getPorcentagemDesconto, RetornoDesconto } from "utils/desconto-utils";
import { deepReset, isEmpty, roundHalfToEven, roundToTwoDecimalPlaces } from "utils/util";
import { v4 as uuidV4 } from 'uuid';

const newState = (): { pedido: FlowVendaTO; consultados: string[]; clientePossuiEnderecoForaEstado: boolean; pessoasVersion: number; } => {
    return {
        pessoasVersion: 0,
        pedido: {
            uuid: uuidV4(),
            quantidadeTotal: 0,
            quantidadeTotalTroca: 0,
            valorTotalBruto: 0,
            valorTotalLiquido: 0,
            valorTotalLiquidoTroca: 0,
            finalidadePedido: null,
            emissao: '',
            entrega: '',
            numero: '',
            numeroEntrega: 1,
            statusPedido: StatusPedido.ABERTO,
            cliente: null,
            vendedorPrincipal: null,
            outrosVendedores: [],
            guias: [],
            formaComissao: null,
            parcelas: [],
            condicoesPagamento: [],
            itens: [],
            itensTroca: [],
            cpfNota: null,
            itensPagamento: [],
            flowItensTroca: [],
            itensPagamentoDevolucao: [],
            consultados: [],
            tabelaPreco: null,
            pedidoDescontoAcrescimo: {
                uuidAutorizador: null,
                valorDesconto: 0,
                percentualDesconto: 0,
                tipoDesconto: TipoDesconto.PERCENTUAL,
                acrescimo: 0,
            },
            deposito: null,
            estabelecimento: {
                nomeFantasia: "",
                telefone: "",
                endereco: "",
                inscricaoEstadual: "",
                complemento: "",
                municipio: "",
                email: "",
                cep: "",
            },
            observacao: "",
            observacaoFixa: "",
            flowItens: {
                produtos: [],
                condicaoPagamentos: [],
                dadosCartaoTOS: [],
                dadosChequeTOS: []
            },
            transportador: {
                frete: null,
                transportadora: null,
                placa: null,
                uf: null,
                pesoLiquido: 0,
                pesoBruto: 0,
                quantidade: 0,
                especie: null,
                marca: null,
                numeroVolumes: null,
                valorFrete: 0,
                valorSeguro: 0,
                observacao: null,
            },
            parcelasCrediario: [],
            troco: null,
            canalDeVenda: null,
            pedidosAgrupados: []
        },
        consultados: [],
        clientePossuiEnderecoForaEstado: false,
    };
};

export const stateSlice = createSlice({
    name: 'state',
    initialState: newState(),
    reducers: {
        setPedido: (s, { payload }: PayloadAction<FlowVendaTO>) => {
            s.pedido = payload;
            updateFooter(s.pedido);
        },
        setStateByProps: ({ pedido }, { payload }: PayloadAction<FlowVendaTO>) => {
            payload.itensTroca.forEach(e => e.isTroca = true);
            pedido.uuid = payload.uuid;
            pedido.numero = payload.numero;
            pedido.cliente = payload.cliente;
            pedido.vendedorPrincipal = payload.vendedorPrincipal;
            pedido.outrosVendedores = payload.outrosVendedores;
            pedido.guias = payload.guias;
            pedido.itens = payload.itens;
            pedido.itensTroca = payload.itensTroca;
            pedido.pedidoDescontoAcrescimo = payload.pedidoDescontoAcrescimo;
            pedido.cpfNota = payload.cpfNota;
            pedido.observacao = payload.observacao;
            pedido.canalDeVenda = payload.canalDeVenda;
            pedido.tabelaPreco = payload.tabelaPreco;
            pedido.pedidosAgrupados = payload.pedidosAgrupados;

            recalculaValorDescontoFooter(pedido);
        },
        resetState: s => {
            const pessoasVersion = s.pessoasVersion;
            deepReset(s, newState());
            s.pessoasVersion = pessoasVersion;
        },
        pessoaSalva(s) {
            s.pessoasVersion += 1;
        },
        setAbstractModel: ({ pedido }, { payload }: PayloadAction<{ propertieName: AbstractModel<FlowVendaTO, IAbstractModel | null>; obj: IAbstractModel | null; }>) => {
            pedido[payload.propertieName] = payload.obj;
        },
        resetAbstractModel: ({ pedido }, { payload }: PayloadAction<AbstractModel<FlowVendaTO, IAbstractModel | null>>) => {
            pedido[payload] = null;
        },
        setCpfNota: ({ pedido }, { payload }: PayloadAction<ICpfNota | null>) => {
            pedido.cpfNota = payload;
        },
        setCliente: ({ pedido }, { payload }: PayloadAction<IConsumidor>) => {
            pedido.cliente = payload;
        },
        addEstoqueItens: (s, { payload }: PayloadAction<{ itens: Array<EstoqueTO>; }>) => {
            s.pedido.itens.forEach(e => {
                const foundedItemInResponse = payload.itens.find(el => e.produto.uuid === el.sku.uuid);
                if (e.estoque === -1) e.estoque = 0;
                if (foundedItemInResponse && parseFloat(foundedItemInResponse.saldo) >= 0) e.estoque = Number(foundedItemInResponse.saldo.replace(',', '.'));
                if (!s.pedido.consultados.includes(e.produto.uuid)) s.pedido.consultados.push(e.produto.uuid);
            });
        },
        atualizarEstoqueItens: s => {
            const adicionadosAnteriormente = s.pedido.itens.filter(p => p.estoque !== -1);
            s.pedido.itens.forEach(p => {
                const newItem = p.estoque === -1;
                if (newItem) {
                    const findedItemJaAdicionado = adicionadosAnteriormente.find(a => a.produto.uuid === p.produto.uuid);
                    p.estoque = findedItemJaAdicionado?.estoque ?? 0;
                }
            });
        },
        removeEstoqueItens: (s, { payload }: PayloadAction<IProdutoRow>) => {
            s.pedido.itens.forEach(e => {
                s.pedido.consultados.splice(s.pedido.consultados.indexOf(payload.produto.uuid), 1);
            });
        },
        addSku: ({ pedido }, { payload }: PayloadAction<IProdutoRow>) => {
            payload.valorTotal = roundHalfToEven(roundToTwoDecimalPlaces(payload.valorTotal), 2);
            pedido.itens.unshift(payload);
            recalculaValorDescontoFooter(pedido);
        },
        addSkuTroca: ({ pedido }, { payload }: PayloadAction<IProdutoRow>) => {
            payload.valorTotal = roundHalfToEven(roundToTwoDecimalPlaces(payload.valorTotal), 2);
            pedido.itensTroca.unshift(payload);
            recalculaValorDescontoFooter(pedido);
        },
        // removeSku
        setProdutos: ({ pedido }, { payload }: PayloadAction<Array<IProdutoRow>>) => {
            pedido.itens = payload;
            recalculaValorDescontoFooter(pedido);
        },
        // removeSkuTroca
        setProdutosTroca: ({ pedido }, { payload }: PayloadAction<Array<IProdutoRow>>) => {
            pedido.itensTroca = payload;
            recalculaValorDescontoFooter(pedido);
        },
        setQuantidadeItem({ pedido }, { payload }: PayloadAction<{ uuid: string, quantidade: number; }>) {
            const find = pedido.itens.find(e => e.uuid === payload.uuid);
            if (find) {
                find.quantidade = payload.quantidade;
                recalculaValorDescontoRow(find);
                recalculaValorDescontoFooter(pedido);
            }
        },
        setQuantidadeItemTroca({ pedido }, { payload }: PayloadAction<{ uuid: string, quantidade: number; }>) {
            const find = pedido.itensTroca.find(e => e.uuid === payload.uuid);
            if (find) {
                find.quantidade = payload.quantidade;
                recalculaValorDescontoRow(find);
                recalculaValorDescontoFooter(pedido);
            }
        },
        setDescontoItem: ({ pedido }, { payload }: PayloadAction<{ uuid: string; value: number; }>) => {
            const find = pedido.itens.find(e => e.uuid === payload.uuid);
            if (find) {
                setDescItem(find, payload.value);
                recalculaValorDescontoFooter(pedido);
            }
        },
        setDescontoItemTroca: ({ pedido }, { payload }: PayloadAction<{ uuid: string; value: number; }>) => {
            const find = pedido.itensTroca.find(e => e.uuid === payload.uuid);
            if (find) {
                setDescItem(find, payload.value);
                updateFooterTroca(pedido);
            }
        },
        toggleTipoDescontoItem: ({ pedido }, { payload }: PayloadAction<{ uuid: string; }>) => {
            const find = pedido.itens.find(e => e.uuid === payload.uuid);
            if (find) {
                find.itemDesconto.tipoDesconto = find.itemDesconto.tipoDesconto === TipoDesconto.PERCENTUAL ? TipoDesconto.VALOR : TipoDesconto.PERCENTUAL;
            }
        },
        toggleTipoDescontoItemTroca: ({ pedido }, { payload }: PayloadAction<{ uuid: string; }>) => {
            const find = pedido.itensTroca.find(e => e.uuid === payload.uuid);
            if (find) {
                find.itemDesconto.tipoDesconto = find.itemDesconto.tipoDesconto === TipoDesconto.PERCENTUAL ? TipoDesconto.VALOR : TipoDesconto.PERCENTUAL;
            }
        },
        setDescontoFooter: ({ pedido }, { payload }: PayloadAction<{ value: number; }>) => {
            recalculaValorTotalBruto(pedido);
            setDescFooter(pedido, payload.value);
        },
        resetDescontoFooter: (s) => {
            const state = newState();
            s.pedido.pedidoDescontoAcrescimo.valorDesconto = state.pedido.pedidoDescontoAcrescimo.valorDesconto;
            s.pedido.pedidoDescontoAcrescimo.percentualDesconto = state.pedido.pedidoDescontoAcrescimo.percentualDesconto;
            updateFooter(s.pedido);
        },
        resetAcrescimoFooter: (s) => {
            s.pedido.pedidoDescontoAcrescimo.acrescimo = 0
        },
        toogleTipoDescontFooter: ({ pedido }) => {
            pedido.pedidoDescontoAcrescimo.tipoDesconto = pedido.pedidoDescontoAcrescimo.tipoDesconto === TipoDesconto.PERCENTUAL ? TipoDesconto.VALOR : TipoDesconto.PERCENTUAL;
        },
        setValorLiquido: (s, { payload }: PayloadAction<{ value: number; sourceEvent: SourceType; }>) => {
            const { pedido } = s;
            if (payload.sourceEvent === 'event') {
                const valorDesconto = (pedido.valorTotalBruto - payload.value) - pedido.valorTotalLiquidoTroca;
                pedido.pedidoDescontoAcrescimo.percentualDesconto = (valorDesconto / pedido.valorTotalBruto) * 100;
                pedido.pedidoDescontoAcrescimo.valorDesconto = valorDesconto;
                updateFooter(pedido);
            }
        },
        changeTabelaPrecoPadraoPedido: ({ pedido }, { payload }: PayloadAction<IAbstractModel>) => {
            pedido.tabelaPreco = payload;
        },
        atualizaValoresItensPedido: ({ pedido }, { payload }: PayloadAction<Array<IProduto | null>>) => {
            const atualizarValores = (itens: IProdutoRow[]) => {
                for (let i = itens.length - 1; i >= 0; i--) {
                    const novoItem = payload.find(el => itens[i].produto.uuid === el?.uuid);
                    if (!novoItem) {
                        itens.splice(i, 1);
                    }
                    else {
                        const novoPreco = novoItem.preco;
                        const item = itens[i];
                        const { valorDesconto, valorTotalLiquido } = getDescItem(
                            item.quantidade,
                            novoPreco,
                            item.itemDesconto.percentualDesconto,
                            TipoDesconto.PERCENTUAL
                        );
                        item.produto.preco = novoPreco;
                        item.valorTotal = valorTotalLiquido;
                        item.itemDesconto.valorDesconto = valorDesconto;
                    }
                }
            }

            atualizarValores(pedido.itens);
            atualizarValores(pedido.itensTroca);
            recalculaValorDescontoFooter(pedido);
            updateFooter(pedido);
            updateFooterTroca(pedido);
        },
        resetSkusConsultados: (s) => {
            s.pedido.consultados = [];
        },
        addItemDesconto({ pedido }, { payload }: PayloadAction<{ uuid: string, itemDesconto: Omit<PedidoItemDesconto, 'acrescimo'>; }>) {
            const find = pedido.itens.find(e => e.uuid === payload.uuid)!;
            find.itemDesconto.uuidAutorizador = payload.itemDesconto.uuidAutorizador;
        },
        addPedidoDescontoAcrescimo({ pedido }, { payload }: PayloadAction<{ itemDesconto: PedidoItemDesconto; }>) {
            pedido.pedidoDescontoAcrescimo = payload.itemDesconto;
            updateFooter(pedido);
        },
        setAcrescimo({ pedido },{ payload }: PayloadAction<number>) {
            pedido.pedidoDescontoAcrescimo.acrescimo = payload;
            updateFooter(pedido);
        },
        setTransportador({ pedido }, { payload }: PayloadAction<TDadosTransportador>) {
            pedido.transportador = payload;
            updateFooter(pedido);
        },
        setVendedorPrincipal({ pedido }, { payload }: PayloadAction<FlowVendedorDTO | null>) {
            pedido.vendedorPrincipal = payload;
        },
        setOutrosVendedores({ pedido }, { payload }: PayloadAction<FlowVendedorDTO[]>) {
            pedido.outrosVendedores = payload;
        },
        setGuias({ pedido }, { payload }: PayloadAction<FlowVendedorDTO[]>) {
            pedido.guias = payload;
        },
        setBrinde({ pedido }, { payload }: PayloadAction<string>) {
            pedido.itens = pedido.itens.map((item) =>
                item.uuid === payload ? { ...item, brinde: !item.brinde } : item
            );
            updateFooter(pedido);
            recalculaValorDescontoFooter(pedido);
        },
        removeSku({ pedido }, { payload }: PayloadAction<IProdutoRow>) {
            const index = pedido.itens.findIndex(e => e.produto.codigoSku === payload.produto.codigoSku);
            if (index !== -1) {
                const item = pedido.itens.find((_, i) => i === index);
                if (item) {
                    if (item.quantidade > 1) {
                        item.quantidade -= 1;
                    } else {
                        pedido.itens = pedido.itens.filter((_, i) => i !== index);
                    }
                }
                recalculaValorDescontoFooter(pedido);
                updateFooter(pedido);
            }
        },
        removeSkuTrocas({ pedido }, { payload }: PayloadAction<IProdutoRow>) {
            const index = pedido.itensTroca.findIndex(e => e.produto.codigoSku === payload.produto.codigoSku);
            if (index !== -1) {
                const item = pedido.itensTroca.find((_, i) => i === index);
                if (item) {
                    if (item.quantidade > 1) {
                        item.quantidade -= 1;
                    } else {
                        pedido.itensTroca = pedido.itensTroca.filter((_, i) => i !== index);
                    }
                }
                recalculaValorDescontoFooter(pedido);
                updateFooterTroca(pedido);
            }
        },
        setClientePossuiEnderecoForaEstado(state, { payload }: PayloadAction<boolean>) {
            state.clientePossuiEnderecoForaEstado = payload;
        },
        setObservacao(state, { payload }: PayloadAction<string>) {
            state.pedido.observacao = payload;
        },
        setCanalVenda(state, { payload }: PayloadAction<IAbstractModel | null>) {
            state.pedido.canalDeVenda = payload;
        },
        resetDadosFrete(s) {
            const state = newState();
            s.pedido.transportador = state.pedido.transportador;
        }
    }
});

const preferenciasInitialState: Preferencias = {
    transportadoraPadrao: null,
    condicoesPagamento: null,
    tabelasPreco: null,
    formasPagamento: null
};

export const preferenciasSlice = createSlice({
    name: 'preferencias',
    initialState: preferenciasInitialState,
    reducers: {
        setPreferencias(state, { payload }: PayloadAction<Preferencias | null>) {
            state.transportadoraPadrao = payload?.transportadoraPadrao ?? null;
            state.condicoesPagamento = payload?.condicoesPagamento ?? null;
            state.tabelasPreco = payload?.tabelasPreco ?? null;
            state.formasPagamento = payload?.formasPagamento ?? null;
        },
        resetPreferencias() {
            return preferenciasInitialState;
        }
    }
});

export const { setPreferencias, resetPreferencias } = preferenciasSlice.actions;
export const preferenciasReducer = preferenciasSlice.reducer;

export const {
    setPedido,
    setStateByProps,
    resetState,
    pessoaSalva,
    setAbstractModel,
    resetAbstractModel,
    addEstoqueItens,
    removeEstoqueItens,
    addSku,
    addSkuTroca,
    setProdutos,
    setProdutosTroca,
    toggleTipoDescontoItem,
    toggleTipoDescontoItemTroca,
    setQuantidadeItem,
    setQuantidadeItemTroca,
    setDescontoItem,
    setDescontoItemTroca,
    toogleTipoDescontFooter,
    setDescontoFooter,
    setValorLiquido,
    setCpfNota,
    setCliente,
    resetDescontoFooter,
    changeTabelaPrecoPadraoPedido,
    atualizaValoresItensPedido,
    resetSkusConsultados,
    atualizarEstoqueItens,
    addItemDesconto,
    addPedidoDescontoAcrescimo,
    resetAcrescimoFooter,
    setTransportador,
    setVendedorPrincipal,
    setOutrosVendedores,
    setGuias,
    setBrinde,
    removeSku,
    removeSkuTrocas,
    setClientePossuiEnderecoForaEstado,
    setObservacao,
    setCanalVenda,
    resetDadosFrete,
    setAcrescimo
} = stateSlice.actions;
export const stateReducer = stateSlice.reducer;


// FAVOR MANTER MÉTODOS ORGANIZADOS POR SEÇÃO
//================================ FOOTER ================================//
/**
 * Atualiza:
 * quantidadeTotal
 * valorTotalBruto
 * valorTotalLiquido
 */

const updateFooter = (pedido: FlowVendaTO): void => {
    const initial = {
        quantidadeTotal: 0,
        valorTotalBruto: 0,
        valorTotalLiquido: 0,
        valorDesconto: pedido.pedidoDescontoAcrescimo.valorDesconto,
    };

    const novoPedido = pedido.itens.reduce((acc, row) => {
        acc.quantidadeTotal += !row.brinde ? row.quantidade : 0;
        acc.valorTotalBruto += roundHalfToEven(roundToTwoDecimalPlaces(row.valorTotal), 2);
        acc.valorTotalLiquido = acc.valorTotalBruto - acc.valorDesconto;
        return acc;
    }, initial);


    novoPedido.valorTotalLiquido = roundHalfToEven(roundToTwoDecimalPlaces(novoPedido.valorTotalLiquido + (pedido.pedidoDescontoAcrescimo.acrescimo) + (pedido.transportador.valorFrete ?? 0) + (pedido.transportador.valorSeguro ?? 0)), 2);
    Object.assign(pedido, novoPedido);
};

const updateFooterTroca = (pedido: FlowVendaTO): void => {
    const initial = {
        quantidadeTotalTroca: 0,
        valorTotalLiquidoTroca: 0
    };

    const novoPedido = pedido.itensTroca.reduce((acc, row) => {
        acc.quantidadeTotalTroca += row.quantidade;
        acc.valorTotalLiquidoTroca += roundHalfToEven(roundToTwoDecimalPlaces(row.valorTotal), 2);
        return acc;
    }, initial);
    Object.assign(pedido, novoPedido);
};

const setDescFooterAuto = (pedido: FlowVendaTO) => {
    const desc = pedido.pedidoDescontoAcrescimo;
    setDescFooter(pedido, desc.tipoDesconto === TipoDesconto.PERCENTUAL ? desc.percentualDesconto : desc.valorDesconto)
}

const setDescFooter = (pedido: FlowVendaTO, valor: number) => {
    if (pedido.pedidoDescontoAcrescimo.tipoDesconto === TipoDesconto.PERCENTUAL) {
        setPorcentagemDescontoFooter(pedido, valor);
    } else {
        setValorDescontoFooter(pedido, valor);
    }
};

// total bruto já ignora o brinde
const setPorcentagemDescontoFooter = (pedido: FlowVendaTO, percentualDesconto: number) => {
    pedido.pedidoDescontoAcrescimo.percentualDesconto = percentualDesconto;
    pedido.pedidoDescontoAcrescimo.valorDesconto = calcularValorDesconto(pedido.valorTotalBruto, percentualDesconto);
    updateFooter(pedido);
    updateFooterTroca(pedido);
};

// total bruto já ignora o brinde
const setValorDescontoFooter = (pedido: FlowVendaTO, valorDesconto: number) => {
    pedido.pedidoDescontoAcrescimo.valorDesconto = valorDesconto;
    pedido.pedidoDescontoAcrescimo.percentualDesconto = calcularPorcentagemDesconto(pedido.valorTotalBruto, valorDesconto);
    updateFooter(pedido);
    updateFooterTroca(pedido);
};

/**
 * mantem o percentualDesconto
 * atualiza o valorDesconto
 * atualiza o valorTotalLiquido
 */
const recalculaValorDescontoFooter = (pedido: FlowVendaTO) => {
     if (isEmpty(pedido.itens)) {
        pedido.pedidoDescontoAcrescimo.acrescimo = 0;
    }
    recalculaValorTotalBruto(pedido);
    setDescFooterAuto(pedido);
};

const recalculaValorTotalBruto = (pedido: FlowVendaTO) => {
    const valorTotalBruto = pedido.itens.filter(s => !s.brinde).map(s => (s.produto.preco * s.quantidade) - s.itemDesconto.valorDesconto).reduce((acc, row) => acc += row, 0);
    pedido.valorTotalBruto = roundHalfToEven(valorTotalBruto, 2);
};

//================================ ROW ================================//
const setDescItem = (produto: IProdutoRow, valor: number) => {
    const valores = getDescItem(produto.quantidade, produto.produto.preco, valor, produto.itemDesconto.tipoDesconto);
    setValores(produto, valores);
};

/**
 * mantem o percentualDesconto do sku
 * atualiza o valorDesconto do sku
 * atualiza o valorTotal do sku
 */
const recalculaValorDescontoRow = (produto: IProdutoRow) => {
    const valores = getPorcentagemDesconto(produto.quantidade, produto.produto.preco, produto.itemDesconto.percentualDesconto);
    setValores(produto, valores);
};

const setValores = (produto: IProdutoRow, { percentualDesconto, valorDesconto, valorTotalLiquido }: RetornoDesconto) => {
    produto.itemDesconto.percentualDesconto = percentualDesconto;
    produto.itemDesconto.valorDesconto = valorDesconto;
    produto.valorTotal = valorTotalLiquido;
};
